import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

import { FxIcon, FxSpinner } from '@/components/fx'
import { useBlobUrl } from '@/hooks/useBlobUrl'
import { cn } from '@/lib/utils'
import type { Position } from '@/types/domain'

/**
 * The coordinate frame a caller's boxes are given in.
 *
 * `'image'` means the page image's own pixels — the frame the worker writes
 * a result field's `x/y/x2/y2` in, and therefore the one the analysed and
 * enhanced page images are drawn with. Anything else is an explicit size:
 * the template page's own, larger frame for a `boxConfiguration.position`,
 * or a detector's `imageSize` for a fresh suggestion.
 *
 * It is a required part of the API rather than an assumption inside the
 * component because there are three of these frames in play and a box drawn
 * against the wrong one still looks like a box — it just sits somewhere
 * arbitrary, which reads as a recognition failure rather than as the unit
 * error it is. Naming the frame at the call site is the only place the
 * mistake is visible.
 */
export type ViewerFrame = 'image' | { width: number; height: number }

export interface ViewerBox {
  id: string
  label: string
  /** The rectangle, in the frame named by the viewer's `frame` prop. */
  rect: Position
  tone?: 'default' | 'success' | 'warning' | 'danger'
}

const TONE_STROKE = {
  default: 'stroke-primary',
  success: 'stroke-success',
  warning: 'stroke-warning',
  danger: 'stroke-danger',
} as const

const TONE_FILL = {
  default: 'fill-transparent',
  success: 'fill-transparent',
  warning: 'fill-transparent',
  // Only the unclear boxes are tinted: on a full page of rectangles a fill on
  // every one of them hides the scan underneath, and the scan is the evidence.
  danger: 'fill-danger/20',
} as const

const MIN_SCALE = 1
const MAX_SCALE = 6
const ZOOM_STEP = 1.4

interface Transform {
  scale: number
  /** Pan, in CSS pixels of the viewport, before the scale is applied. */
  x: number
  y: number
}

const IDENTITY: Transform = { scale: 1, x: 0, y: 0 }

/**
 * Keeps the page inside its own viewport.
 *
 * At scale s the image overhangs the viewport by (s-1)/2 of its size on each
 * side, so that is exactly how far it may be panned before empty ground
 * appears on the opposite edge. Clamping here rather than while dragging
 * means a zoom-out can never leave the page parked off-screen — the bug the
 * Flutter viewer works around with an "Einpassen" button and an infinite
 * boundary margin.
 */
function clampPan(next: Transform, viewport: { width: number; height: number }): Transform {
  const maxX = (Math.max(next.scale, 1) - 1) * viewport.width * 0.5
  const maxY = (Math.max(next.scale, 1) - 1) * viewport.height * 0.5
  return {
    scale: next.scale,
    x: Math.min(maxX, Math.max(-maxX, next.x)),
    y: Math.min(maxY, Math.max(-maxY, next.y)),
  }
}

export interface PageViewerProps {
  /** Path under `GET /v1/files/*path`; fetched with the session's token. */
  imagePath: string
  boxes?: ViewerBox[]
  frame?: ViewerFrame
  selectedBoxId?: string | null
  onSelectBox?: (id: string) => void
  /** The page itself is decorative when a list beside it carries the same
   *  information; pass a description when it is not. */
  alt?: string
  showLabels?: boolean
  /** The zoom controls. Off where the page is a thumbnail-sized preview and
   *  selection runs entirely through the list beside it. */
  controls?: boolean
  className?: string
}

/**
 * One page image with boxes over it, zoomable and pannable.
 *
 * It does not build on `BoxOverlay`, which draws its own image and pins its
 * `viewBox` to that image's natural size: neither an explicit coordinate
 * frame nor a selected box nor a zoom transform can be expressed through it.
 * `BoxOverlay` stays as it is for the screens that only need a static
 * preview; this is the viewer the five screens that need a selection share.
 */
export function PageViewer({
  imagePath,
  boxes = [],
  frame = 'image',
  selectedBoxId,
  onSelectBox,
  alt,
  showLabels = false,
  controls = true,
  className,
}: PageViewerProps) {
  const { url, error } = useBlobUrl(imagePath)
  const [natural, setNatural] = useState<{ width: number; height: number } | null>(null)
  const [transform, setTransform] = useState<Transform>(IDENTITY)
  const viewport = useRef<HTMLDivElement | null>(null)
  const drag = useRef<{ pointerId: number; startX: number; startY: number; from: Transform } | null>(
    null,
  )

  const viewportSize = useCallback(() => {
    const node = viewport.current
    return { width: node?.clientWidth ?? 0, height: node?.clientHeight ?? 0 }
  }, [])

  // A new page starts fitted. Without this a viewer left zoomed into the
  // bottom of one scan opens the next one on a patch of empty paper.
  useLayoutEffect(() => {
    setTransform(IDENTITY)
    setNatural(null)
  }, [imagePath])

  const zoomBy = useCallback(
    (factor: number) => {
      setTransform((current) => {
        const scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, current.scale * factor))
        // Zooming happens about the middle of the viewport, so the pan has to
        // grow with the scale or the point under the centre drifts away.
        const ratio = scale / current.scale
        return clampPan({ scale, x: current.x * ratio, y: current.y * ratio }, viewportSize())
      })
    },
    [viewportSize],
  )

  const fit = useCallback(() => setTransform(IDENTITY), [])

  const frameSize =
    frame === 'image' ? (natural ?? { width: 0, height: 0 }) : frame

  /**
   * Brings a box to the middle of the viewport, keeping the current zoom.
   *
   * Only while zoomed in: at fit scale the whole page is on screen already,
   * and a "centre" that moves nothing would make selecting a chip feel
   * broken.
   */
  const centreOn = useCallback(
    (box: ViewerBox, view: { width: number; height: number }) => {
      setTransform((current) => {
        if (current.scale <= 1) return current
        const box01 = {
          x: (box.rect.x + box.rect.width / 2) / Math.max(1, frameSize.width),
          y: (box.rect.y + box.rect.height / 2) / Math.max(1, frameSize.height),
        }
        return clampPan(
          {
            scale: current.scale,
            x: (0.5 - box01.x) * view.width * current.scale,
            y: (0.5 - box01.y) * view.height * current.scale,
          },
          view,
        )
      })
    },
    // `frameSize` is derived from `frame` and `natural`, so those two are
    // what this callback actually depends on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [frame, natural],
  )

  useEffect(() => {
    if (!selectedBoxId) return
    const box = boxes.find((entry) => entry.id === selectedBoxId)
    if (box) centreOn(box, viewportSize())
    // Re-centring on every `boxes` identity change would fight a parent that
    // rebuilds the array each render; the selection is what should move the
    // page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBoxId])

  // A document whose page was never rendered has no path to fetch. Without
  // its own branch that falls into the loading state below and spins for
  // ever, beside a result that is finished.
  if (!imagePath) {
    return (
      <div className="grid min-h-48 place-items-center rounded-sm bg-surface p-8 text-body text-text-muted shadow-raised">
        Für diese Seite liegt kein Bild vor.
      </div>
    )
  }

  if (error) {
    return (
      <div className="grid min-h-48 place-items-center rounded-sm bg-surface p-8 text-body text-text-muted shadow-raised">
        Seitenbild konnte nicht geladen werden.
      </div>
    )
  }

  if (!url) {
    return (
      <div className="grid min-h-48 place-items-center rounded-sm bg-surface p-8 shadow-raised">
        <FxSpinner label="Seite wird geladen …" />
      </div>
    )
  }

  const zoomed = transform.scale > 1
  const showBoxes = frameSize.width > 0 && frameSize.height > 0

  return (
    <div className={cn('relative', className)}>
      <div
        ref={viewport}
        className={cn(
          'relative w-full overflow-hidden rounded-sm bg-surface shadow-raised',
          zoomed ? 'cursor-grab touch-none active:cursor-grabbing' : undefined,
        )}
        style={natural ? { aspectRatio: `${natural.width} / ${natural.height}` } : undefined}
        onPointerDown={(event) => {
          if (!zoomed || event.button !== 0) return
          drag.current = {
            pointerId: event.pointerId,
            startX: event.clientX,
            startY: event.clientY,
            from: transform,
          }
          event.currentTarget.setPointerCapture(event.pointerId)
        }}
        onPointerMove={(event) => {
          const active = drag.current
          if (!active || active.pointerId !== event.pointerId) return
          const next = {
            scale: active.from.scale,
            x: active.from.x + (event.clientX - active.startX),
            y: active.from.y + (event.clientY - active.startY),
          }
          setTransform(clampPan(next, viewportSize()))
        }}
        onPointerUp={(event) => {
          if (drag.current?.pointerId === event.pointerId) drag.current = null
        }}
        onPointerCancel={() => {
          drag.current = null
        }}
      >
        <div
          className="h-full w-full origin-center"
          style={{
            transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})`,
          }}
        >
          <img
            src={url}
            alt={alt ?? ''}
            draggable={false}
            className="block h-full w-full select-none"
            onLoad={(event) =>
              setNatural({
                width: event.currentTarget.naturalWidth,
                height: event.currentTarget.naturalHeight,
              })
            }
          />
          {showBoxes && (
            <svg
              viewBox={`0 0 ${frameSize.width} ${frameSize.height}`}
              // The image is laid out to fill its box, so the overlay must be
              // stretched the same way rather than letterboxed.
              preserveAspectRatio="none"
              className="absolute inset-0 h-full w-full"
              // The page is a picture of what the list beside it already says.
              // Clicking a box is a shortcut for people using a mouse; the
              // chip list is the operable control, and it is keyboard-complete.
              aria-hidden="true"
            >
              {boxes.map((box) => {
                const selected = box.id === selectedBoxId
                return (
                  <g
                    key={box.id}
                    onClick={onSelectBox ? () => onSelectBox(box.id) : undefined}
                    className={onSelectBox ? 'cursor-pointer' : undefined}
                  >
                    <rect
                      x={box.rect.x}
                      y={box.rect.y}
                      width={box.rect.width}
                      height={box.rect.height}
                      className={cn(TONE_FILL[box.tone ?? 'default'], TONE_STROKE[box.tone ?? 'default'])}
                      // A stroke in viewBox units is a hairline on a 2400px
                      // scan and a slab on a thumbnail, and it changes width
                      // as the page is zoomed. This one is 2 CSS pixels at
                      // every scale.
                      vectorEffect="non-scaling-stroke"
                      strokeWidth={2}
                    />
                    {selected && (
                      <rect
                        x={box.rect.x}
                        y={box.rect.y}
                        width={box.rect.width}
                        height={box.rect.height}
                        className="fill-transparent stroke-primary-strong"
                        vectorEffect="non-scaling-stroke"
                        strokeWidth={4}
                      />
                    )}
                    {showLabels && (
                      <text
                        x={box.rect.x}
                        y={Math.max(box.rect.y - frameSize.width / 300, frameSize.width / 120)}
                        className="fill-primary"
                        style={{ fontSize: frameSize.width / 70 }}
                      >
                        {box.label}
                      </text>
                    )}
                  </g>
                )
              })}
            </svg>
          )}
        </div>
      </div>

      {controls && (
        <div className="absolute right-2 top-2 flex items-center gap-1 rounded-md bg-surface px-1 py-1 shadow-overlay">
          <ZoomButton
            icon="zoom_out"
            label="Verkleinern"
            disabled={transform.scale <= MIN_SCALE}
            onClick={() => zoomBy(1 / ZOOM_STEP)}
          />
          <span className="min-w-12 text-center text-caption tabular-nums text-text-muted">
            {Math.round(transform.scale * 100)} %
          </span>
          <ZoomButton
            icon="zoom_in"
            label="Vergrößern"
            disabled={transform.scale >= MAX_SCALE}
            onClick={() => zoomBy(ZOOM_STEP)}
          />
          <ZoomButton icon="fit_screen" label="Einpassen" disabled={!zoomed} onClick={fit} />
        </div>
      )}
    </div>
  )
}

function ZoomButton({
  icon,
  label,
  onClick,
  disabled,
}: {
  icon: string
  label: string
  onClick: () => void
  disabled?: boolean
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className="grid h-8 w-8 place-items-center rounded-md text-text-muted hover:bg-ground disabled:text-text-disabled"
    >
      <FxIcon name={icon} size={20} />
    </button>
  )
}
